import { useEffect, useMemo, useState, useCallback } from "react";
import "./W2InfoPopup.css";
import { toEpochInTZ } from "../../../utils/datesBR";
import { confirmarMetaW2 } from "../api";

function extractYMD(value) {
  if (!value) return null;
  const text = String(value).trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
}

function toHHMM(raw) {
  if (!raw && raw !== 0) return "-";
  const text = String(raw).trim();
  if (!text) return "-";
  const match = text.match(/^(\d{1,3}):([0-5]\d)(?::([0-5]\d))?$/);
  if (match) return `${String(Number(match[1])).padStart(2, "0")}:${match[2]}`;
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(text)) return text.slice(11, 16);
  return text;
}

function fmtStatus(value) {
  if (!value) return "-";
  const status = String(value).trim().toUpperCase();
  if (status === "EM_VALIDACAO_AUTOMATICA") return "Pré-programação";
  if (status === "PROGRAMADO") return "Programado";
  return value;
}

function fmtDateTab(ymd) {
  if (!ymd) return "-";
  const match = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}` : ymd;
}

function getDiaSemana(ymd) {
  if (!ymd) return "";
  const dias = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dias[dt.getDay()] || "";
}

function countdownHHMMSSFromMs(expMs, nowMs) {
  if (!Number.isFinite(expMs)) return null;
  const diff = expMs - nowMs;
  if (diff <= 0) return "00:00:00";
  const totalSec = Math.max(1, Math.ceil(diff / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const COLUMNS = [
  { key: "codigo_unico", label: "Código" },
  { key: "status_wf2", label: "Status", fmt: fmtStatus },
  { key: "tipo_programacao", label: "Tipo de Programação" },
  { key: "prioridade", label: "Prioridade" },
  { key: "coordenador", label: "Coordenador" },
  { key: "gerencia", label: "Gerência" },
  { key: "ativo", label: "Ativo" },
  { key: "atividade", label: "Atividade" },
  { key: "quantidade", label: "Quantidade" },
  { key: "sbOrigem", label: "Local de Partida" },
  { key: "sbDeTrabalho", label: "SB de Trabalho" },
  { key: "sbEncerramento", label: "Local de Encerramento" },
  { key: "kmInicio", label: "KM Início" },
  { key: "kmFinal", label: "KM Fim" },
  { key: "horario_do_intervalo", label: "Horário do Intervalo", fmt: toHHMM },
  { key: "tempo_do_intervalo", label: "Tempo do Intervalo", fmt: toHHMM },
  { key: "locomotiva1", label: "Locomotiva 1" },
  { key: "locomotiva2", label: "Locomotiva 2" },
  { key: "observacao", label: "Observação" },
];

export default function W2InfoPopup({
  open,
  loading,
  error,
  data = [],
  onClose,
  onRefresh,
}) {
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [confirmSuccess, setConfirmSuccess] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const filteredData = useMemo(() => {
    const allowed = new Set(["PROGRAMADO", "EM_VALIDACAO_AUTOMATICA"]);
    return (data || [])
      .filter((row) => allowed.has(String(row?.status_wf2 || "").trim().toUpperCase()))
      .sort((a, b) => {
        const sa = String(a?.status_wf2 || "").trim().toUpperCase();
        const sb = String(b?.status_wf2 || "").trim().toUpperCase();
        if (sa === "EM_VALIDACAO_AUTOMATICA" && sb !== "EM_VALIDACAO_AUTOMATICA") return -1;
        if (sb === "EM_VALIDACAO_AUTOMATICA" && sa !== "EM_VALIDACAO_AUTOMATICA") return 1;
        return 0;
      });
  }, [data]);

  const { datasPorDia, datasDisponiveis } = useMemo(() => {
    const grouped = {};
    for (const row of filteredData) {
      const ymd = extractYMD(row?.data);
      if (!ymd) continue;
      if (!grouped[ymd]) grouped[ymd] = [];
      grouped[ymd].push(row);
    }
    return {
      datasPorDia: grouped,
      datasDisponiveis: Object.keys(grouped).sort(),
    };
  }, [filteredData]);

  useEffect(() => {
    if (datasDisponiveis.length > 0 && !datasDisponiveis.includes(selectedDate)) {
      setSelectedDate(datasDisponiveis[0]);
    }
  }, [datasDisponiveis, selectedDate]);

  const dadosDaData = useMemo(() => {
    if (!selectedDate || !datasPorDia[selectedDate]) return [];
    return datasPorDia[selectedDate];
  }, [datasPorDia, selectedDate]);

  const itensPendentes = useMemo(
    () =>
      (dadosDaData || []).filter(
        (row) => String(row?.status_wf2 || "").trim().toUpperCase() === "EM_VALIDACAO_AUTOMATICA"
      ),
    [dadosDaData]
  );

  const selectedExpMs = useMemo(() => {
    const arr = (dadosDaData || [])
      .filter(
        (row) => String(row?.status_wf2 || "").trim().toLowerCase() === "em_validacao_automatica"
      )
      .map((row) => {
        const epoch = Number(row?.pre_meta_expira_em_epoch ?? Number.NaN);
        if (Number.isFinite(epoch)) return epoch;
        return toEpochInTZ(row?.pre_meta_expira_em, "America/Sao_Paulo");
      })
      .filter((value) => Number.isFinite(value));
    return arr.length ? Math.min(...arr) : Number.NaN;
  }, [dadosDaData]);

  useEffect(() => {
    if (!Number.isFinite(selectedExpMs)) return undefined;
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [selectedExpMs]);

  const selectedCountdown = Number.isFinite(selectedExpMs)
    ? countdownHHMMSSFromMs(selectedExpMs, nowMs)
    : null;

  const handleConfirmarTodos = useCallback(async () => {
    if (itensPendentes.length === 0) return;

    setConfirmingAll(true);
    setConfirmError(null);
    setConfirmSuccess(null);

    let confirmados = 0;
    const erros = [];

    for (const row of itensPendentes) {
      const id = row?.id;
      const codigo = row?.codigo_unico;
      if (!id && !codigo) continue;

      try {
        const result = await confirmarMetaW2({ id, codigo_unico: codigo });
        if (result?.ok) confirmados += 1;
        else erros.push(result?.message || `Erro em ${codigo || id}`);
      } catch (err) {
        erros.push(err?.message || `Erro em ${codigo || id}`);
      }
    }

    setConfirmingAll(false);

    if (erros.length > 0) {
      setConfirmError(`${erros.length} erro(s): ${erros.slice(0, 3).join("; ")}`);
    }

    if (confirmados > 0) {
      setConfirmSuccess(
        `${confirmados} ${confirmados === 1 ? "programação confirmada" : "programações confirmadas"}`
      );
      onRefresh?.();
    }
  }, [itensPendentes, onRefresh]);

  if (!open) return null;

  return (
    <div className="w2info-backdrop" onClick={onClose}>
      <div className="w2info-card" onClick={(e) => e.stopPropagation()}>
        <header className="w2info-header">
          <div className="w2info-title">Pré-visualização dos Próximos Dias</div>
          <div className="w2info-headActions only-close">
            <button type="button" className="w2info-close" onClick={onClose} aria-label="Fechar">
              ×
            </button>
          </div>
        </header>

        <div className="w2info-body">
          {loading && <div className="w2info-loading">Carregando dados...</div>}
          {error && <div className="w2info-error">{error}</div>}
          {confirmError && <div className="w2info-error">{confirmError}</div>}
          {confirmSuccess && <div className="w2info-success">{confirmSuccess}</div>}

          {!loading && !error ? (
            <div className="w2info-shell">
              <div className="w2info-tableBar">
                <div className="w2info-dateTabs">
                  {datasDisponiveis.map((ymd) => {
                    const isActive = selectedDate === ymd;
                    return (
                      <button
                        key={ymd}
                        type="button"
                        className={`w2info-dateTab ${isActive ? "active" : ""}`}
                        onClick={() => setSelectedDate(ymd)}
                      >
                        <span className="w2info-dateTab-dia">{getDiaSemana(ymd)}</span>
                        <span className="w2info-dateTab-data">{fmtDateTab(ymd)}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="w2info-barCenter">
                  <span className="w2info-barCount">{dadosDaData.length} programações</span>
                  {selectedCountdown && (
                    <span className="w2info-barCountdown">Pré-programação: {selectedCountdown}</span>
                  )}
                </div>

                <div className="w2info-barActions">
                  {itensPendentes.length > 0 && (
                    <button
                      type="button"
                      className="w2info-confirm-all-btn"
                      disabled={confirmingAll}
                      onClick={handleConfirmarTodos}
                      title={`Confirmar ${itensPendentes.length} programação(ões) pendente(s)`}
                    >
                      {confirmingAll ? "Confirmando..." : `Confirmar (${itensPendentes.length})`}
                    </button>
                  )}
                </div>
              </div>

              {dadosDaData.length === 0 ? (
                <div className="w2info-noData">Sem dados para esta data.</div>
              ) : (
                <div className="w2info-tableWrap">
                  <table className="w2info-table">
                    <thead>
                      <tr>
                        {COLUMNS.map((column) => (
                          <th key={column.key}>{column.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dadosDaData.map((row, idx) => {
                        const rowKey = row?.id || row?.codigo_unico || idx;
                        return (
                          <tr key={rowKey}>
                            {COLUMNS.map((column) => {
                              const rawValue = row?.[column.key];
                              const value = column.fmt ? column.fmt(rawValue) : rawValue ?? "-";
                              return <td key={column.key}>{value === "" ? "-" : value}</td>;
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
